import './polyfills';
import React from "react";
import ReactDOM from 'react-dom';
import { Route, Routes, BrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { ApolloClient, InMemoryCache, ApolloProvider, createHttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from "@apollo/client/link/error";
import { toaster } from 'evergreen-ui';
import store from './store/store.js';
import { Provider } from 'react-redux';
import App from './App';
import Login from "./views/Login/Login.js";
import ResetPassword from "./views/Login/ResetPassword.js";
import ResetNewPassword from "./views/Login/ResetNewPassword.js";
import AcceptInvite from "./views/AcceptInvite.js";
import ThankYou from "./views/ThankYou.js";
import reportWebVitals from './reportWebVitals';
import './assets/styles/index.css';
import './index.css';  // Import global styles
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";

// Set the base URL for GraphQL
const baseUrl = process.env.NODE_ENV === 'development'
  ? process.env.REACT_APP_GRAPHQL_URL
  : `${window.location.protocol}//${window.location.hostname}`;

// Check if running on localhost or 127.0.0.1
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

if (!isLocalhost) { // Only initialize Sentry on stage or production
  Sentry.init({
    dsn: "https://b29aa3e8da966b2daeded4d62bd5707a@o4507947661852672.ingest.us.sentry.io/4507980770181120",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/workinglit\.sentry\.io\/projects/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (let err of graphQLErrors) {
      if (err.extensions && err.extensions.code === "UNAUTHENTICATED") {
        toaster.danger("Your session has expired. Please log in again.", { duration: 5 });
        localStorage.removeItem('token');
        localStorage.removeItem('userid');
        localStorage.removeItem('publisher');
        localStorage.removeItem('publisher_name');
        // Potentially clear other relevant local storage items or Redux state

        // Redirect to login page
        // Ensure this doesn't create a loop if /login also makes GQL requests that could fail this way
        // A simple way to break potential loops is to check if we are already on the login page.
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        // It might be cleaner to use react-router's navigation if possible,
        // but window.location.href is robust for a full redirect and state clear.
        return; // Stop further processing for this error
      }
    }
  }

  // Handle network errors or other GraphQL errors if needed
  // For example, log them or show a generic error message
  if (networkError) {
    console.error(`[Network error]: ${networkError}`, { operation });
    // Optionally show a generic network error toaster
    // toaster.danger("Network error. Please check your connection.", { duration: 5 });
  }
});

// Create HTTP link
const httpLink = createHttpLink({
  uri: `${baseUrl}/graphql-auth/`,
  credentials: 'include', // Include cookies in the requests
});

// Create auth link that adds the token to every request
const authLink = setContext((_, { headers }) => {
  // Get the token from localStorage
  const token = localStorage.getItem('token');

  // Return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      Authorization: token ? `Token ${token}` : '',
    }
  };
});

// Create Apollo Client with the auth link
const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          products: {
            // Use these fields as the cache key
            keyArgs: ['publisher', 'params'],

            // Merge function for handling pagination and updates
            merge(existing = [], incoming, { args: { page = 1 } }) {
              if (page === 1) {
                return incoming;
              }
              // Merge existing and incoming data for pagination
              return [...existing, ...incoming];
            }
          }
        }
      },
      Product: {
        // Unique identifier for Product type
        keyFields: ['id', 'publisher'],
      }
    }
  }),
});

// Authentication checks
const checkAuthentication = () => localStorage.getItem('token') !== null;
const checkUserid = () => localStorage.getItem('userid') || false;
const checkPublisher = () => localStorage.getItem('publisher') || false;
const checkPublisherName = () => localStorage.getItem('publisher_name') || false;

const isAuthenticated = checkAuthentication();
const user = checkUserid();
const publisher = checkPublisher();
const publisher_name = checkPublisherName();

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught in ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong.</h1>;
    }
    return this.props.children;
  }
}

// Add this component to track route changes
const RouteTracker = () => {
  const location = useLocation();

  React.useEffect(() => {
    // Send pageview to Google Analytics
    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search
    });
  }, [location]);

  return null;
};

// Render the application
ReactDOM.render(
  <Provider store={store}>
    <ApolloProvider client={client}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <RouteTracker />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset_password" element={<ResetPassword />} />
          <Route path="/reset_password/:resetToken" element={<ResetNewPassword />} />
          <Route path="/accept_invite" element={<AcceptInvite />} />
          <Route path="/thanks-for-trying-workinglit" element={<ThankYou />} />
          <Route path="*" element={
            isAuthenticated ?
              <App
                isAuthenticated={isAuthenticated}
                user={user}
                publisher={publisher}
                publisherName={publisher_name}
                baseUrl={baseUrl}
              />
              : <Navigate to="/login" />
          } />
        </Routes>
      </BrowserRouter>
    </ApolloProvider>
  </Provider>,
  document.getElementById('root')
);

// Report web vitals
reportWebVitals();
